/**
 * EmployeeCard — full Workday-class HR employee record.
 *
 * Tabbed page: Personal | Job | (12 more tabs land in deploys 2-5).
 * Route: /hr/employees/:id (App.tsx).
 *
 * Deploy 1 (D107): Personal + Job tabs read/write.
 * Deploys 2-5: Banking, Tax/ID, Compensation, Benefits, Time Off,
 *              Emergency, Education, Documents, EEO, Equipment.
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_V1, authFetch } from '../constants';
import PageLayout from '../components/ui/PageLayout';
import TabBar from '../components/ui/TabBar';
import StatusBadge from '../components/ui/StatusBadge';

const API = `${API_V1}/hr`;

interface Employee {
  id: number;
  employee_number?: string | null;
  first_name?: string | null; middle_name?: string | null; last_name?: string | null;
  preferred_name?: string | null; display_name?: string | null;
  email?: string | null; personal_email?: string | null;
  mobile_phone?: string | null; alternate_phone?: string | null;
  date_of_birth?: string | null; gender?: string | null; pronouns?: string | null;
  marital_status?: string | null; nationality?: string | null;
  photo_url?: string | null; time_zone?: string | null;
  // Job
  entity_code?: string | null; department?: string | null;
  job_title?: string | null; job_code?: string | null; job_family?: string | null;
  job_level?: string | null; manager_id?: number | null;
  dotted_line_manager_id?: number | null;
  hire_date?: string | null; original_hire_date?: string | null;
  contract_end_date?: string | null; termination_date?: string | null;
  probation_end?: string | null;
  employment_type?: string | null; flsa_classification?: string | null;
  weekly_hours?: number | null; cost_center?: string | null;
  location?: string | null; work_location?: string | null;
  remote_status?: string | null;
  annual_salary?: number | null; currency?: string | null;
  lifecycle_status?: string | null; version: number;
}

interface Address {
  id?: number; address_type: string;
  street_line1?: string | null; street_line2?: string | null;
  city?: string | null; state_province?: string | null;
  postal_code?: string | null; country?: string | null;
  is_primary?: boolean;
}

const TABS = [
  { key: 'personal', label: 'Personal', enabled: true },
  { key: 'job', label: 'Job & Org', enabled: true },
  { key: 'banking', label: 'Banking', enabled: true },
  { key: 'tax_id', label: 'Tax & ID', enabled: true },
  { key: 'compensation', label: 'Compensation', enabled: true },
  { key: 'benefits', label: 'Benefits', enabled: true },
  { key: 'time_off', label: 'Time Off', enabled: false },
  { key: 'emergency', label: 'Emergency', enabled: true },
  { key: 'education', label: 'Education & Skills', enabled: true },
  { key: 'documents', label: 'Documents', enabled: true },
  { key: 'eeo', label: 'EEO', enabled: true },
  { key: 'equipment', label: 'Equipment', enabled: true },
];

// D108 Banking / Tax & ID types
interface BankAccount {
  id?: number;
  account_holder_name?: string | null; bank_name?: string | null;
  country?: string | null; currency?: string | null;
  account_type?: string | null; payment_method?: string | null;
  account_number_last4?: string | null;
  account_number?: string | null;       // write-only / reveal
  routing_number?: string | null;       // write-only / reveal
  iban?: string | null; swift?: string | null; sort_code?: string | null;
  has_routing?: boolean; has_iban?: boolean; has_swift?: boolean; has_sort_code?: boolean;
  split_pct?: number | null;
  is_primary?: boolean; is_active?: boolean;
}
interface Identification {
  id?: number;
  id_type: string;
  id_number?: string | null;            // write-only / reveal
  id_number_last4?: string | null;
  issuing_country?: string | null;
  issue_date?: string | null;
  expiry_date?: string | null;
  doc_url?: string | null;
  notes?: string | null;
}
interface TaxProfile {
  exists?: boolean;
  tax_residency_country?: string | null;
  filing_status?: string | null;
  withholding_allowances?: number | null;
  additional_withholding?: number | null;
  state_tax_code?: string | null;
  is_exempt?: boolean | null;
  tax_form?: string | null;
}

// D109 Compensation / Benefits / Dependents
interface CompHistoryRow {
  id: number; effective_date: string | null; change_type: string;
  previous_salary: number | null; new_salary: number | null;
  currency: string | null;
  previous_pay_grade: string | null; new_pay_grade: string | null;
  change_pct: number | null; reason: string | null;
}
interface CompChangeForm {
  effective_date?: string; change_type?: string;
  new_salary?: number | null; new_pay_grade?: string;
  reason?: string;
}
interface BenefitRow {
  id?: number; benefit_type: string; plan_name?: string | null;
  carrier?: string | null; policy_number?: string | null;
  coverage_tier?: string | null;
  enrollment_date?: string | null; effective_date?: string | null; end_date?: string | null;
  employee_contribution?: number | null; employer_contribution?: number | null;
  contribution_frequency?: string | null;
  currency?: string | null; status?: string | null; notes?: string | null;
}
interface DependentRow {
  id?: number; first_name?: string | null; last_name?: string | null;
  relationship: string;
  date_of_birth?: string | null; gender?: string | null;
  ssn?: string | null; ssn_last4?: string | null;
  is_covered_health?: boolean; is_covered_dental?: boolean;
  is_covered_vision?: boolean; is_covered_life?: boolean;
  is_active?: boolean;
}

// D110 Emergency / Education / Certifications / Documents / Skills
interface EmergencyContact {
  id?: number; name: string; relationship?: string | null;
  primary_phone?: string | null; alt_phone?: string | null;
  email?: string | null; address?: string | null;
  is_primary?: boolean; priority_order?: number; notes?: string | null;
}
interface EducationRow {
  id?: number; institution?: string | null; degree_type?: string | null;
  field_of_study?: string | null; graduation_year?: number | null;
  gpa?: number | null; country?: string | null;
  doc_url?: string | null; notes?: string | null;
}
interface CertRow {
  id?: number; certification_name: string; issuer?: string | null;
  certification_number?: string | null;
  issue_date?: string | null; expiry_date?: string | null;
  doc_url?: string | null; status?: string | null;
  effective_status?: string | null; days_to_expiry?: number | null;
  is_renewable?: boolean; notes?: string | null;
}
interface DocRow {
  id?: number; document_type: string; document_name?: string | null;
  doc_url?: string | null; file_name?: string | null;
  signed_date?: string | null; expiry_date?: string | null;
  signed_by_employee?: boolean; acknowledged_at?: string | null;
  status?: string | null; effective_status?: string | null;
  days_to_expiry?: number | null; notes?: string | null;
}
interface SkillRow {
  id?: number; skill_name: string; proficiency_level?: string | null;
  years_experience?: number | null; is_primary?: boolean;
}

// D111 EEO + Equipment + RBAC
interface EEOForm {
  exists?: boolean;
  ethnicity?: string | null; race?: string | null;
  veteran_status?: string | null; disability_status?: string | null;
  military_status?: string | null; eeo_job_category?: string | null;
  opt_in?: boolean; disclosure_date?: string | null;
}
interface EquipmentRow {
  id?: number; equipment_type: string;
  make?: string | null; model?: string | null;
  serial_number?: string | null; asset_tag?: string | null;
  assigned_date?: string | null; returned_date?: string | null;
  condition?: string | null;
  estimated_value?: number | null; currency?: string | null;
  doc_url?: string | null; is_active?: boolean; notes?: string | null;
}
interface SectionAccess {
  role: string; visible_sections: string[];
}

export default function EmployeeCard() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<string>('personal');
  const [emp, setEmp] = useState<Employee | null>(null);
  const [home, setHome] = useState<Address>({ address_type: 'HOME' });
  const [mailing, setMailing] = useState<Address>({ address_type: 'MAILING' });
  // D108
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [identifications, setIdentifications] = useState<Identification[]>([]);
  const [taxProfile, setTaxProfile] = useState<TaxProfile>({});
  const [revealedBank, setRevealedBank] = useState<Record<number, BankAccount>>({});
  const [revealedId, setRevealedId] = useState<Record<number, string>>({});
  const [editBank, setEditBank] = useState<BankAccount | null>(null);
  const [editId, setEditId] = useState<Identification | null>(null);
  // D109
  const [compHistory, setCompHistory] = useState<CompHistoryRow[]>([]);
  const [compForm, setCompForm] = useState<CompChangeForm | null>(null);
  const [benefits, setBenefits] = useState<BenefitRow[]>([]);
  const [editBenefit, setEditBenefit] = useState<BenefitRow | null>(null);
  const [dependents, setDependents] = useState<DependentRow[]>([]);
  const [editDep, setEditDep] = useState<DependentRow | null>(null);
  // D110
  const [emergency, setEmergency] = useState<EmergencyContact[]>([]);
  const [editEmerg, setEditEmerg] = useState<EmergencyContact | null>(null);
  const [education, setEducation] = useState<EducationRow[]>([]);
  const [editEdu, setEditEdu] = useState<EducationRow | null>(null);
  const [certs, setCerts] = useState<CertRow[]>([]);
  const [editCert, setEditCert] = useState<CertRow | null>(null);
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [editDoc, setEditDoc] = useState<DocRow | null>(null);
  const [skills, setSkills] = useState<SkillRow[]>([]);
  const [editSkill, setEditSkill] = useState<SkillRow | null>(null);
  // D111
  const [eeo, setEeo] = useState<EEOForm>({ opt_in: false });
  const [equipment, setEquipment] = useState<EquipmentRow[]>([]);
  const [editEquip, setEditEquip] = useState<EquipmentRow | null>(null);
  const [access, setAccess] = useState<SectionAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [eRes, aRes, bRes, iRes, tRes, chRes, beRes, dpRes] = await Promise.all([
        authFetch(`${API}/employees/${id}`),
        authFetch(`${API}/employees/${id}/addresses`),
        authFetch(`${API}/employees/${id}/bank-accounts`),
        authFetch(`${API}/employees/${id}/identifications`),
        authFetch(`${API}/employees/${id}/tax-profile`),
        authFetch(`${API}/employees/${id}/compensation-history`),
        authFetch(`${API}/employees/${id}/benefits`),
        authFetch(`${API}/employees/${id}/dependents`),
      ]);
      if (eRes.ok) setEmp(await eRes.json());
      if (aRes.ok) {
        const d = await aRes.json();
        const addrs: Address[] = d.addresses || [];
        const h = addrs.find(a => (a.address_type || '').toUpperCase() === 'HOME');
        const m = addrs.find(a => (a.address_type || '').toUpperCase() === 'MAILING');
        if (h) setHome(h); if (m) setMailing(m);
      }
      if (bRes.ok) { const d = await bRes.json(); setBankAccounts(d.accounts || []); }
      if (iRes.ok) { const d = await iRes.json(); setIdentifications(d.identifications || []); }
      if (tRes.ok) { setTaxProfile(await tRes.json()); }
      if (chRes.ok) { const d = await chRes.json(); setCompHistory(d.history || []); }
      if (beRes.ok) { const d = await beRes.json(); setBenefits(d.benefits || []); }
      if (dpRes.ok) { const d = await dpRes.json(); setDependents(d.dependents || []); }
      // D110 -- fetched lazily but shipped together to keep load() coherent
      const [emRes, eduRes, certRes, docRes, sklRes] = await Promise.all([
        authFetch(`${API}/employees/${id}/emergency-contacts`),
        authFetch(`${API}/employees/${id}/education`),
        authFetch(`${API}/employees/${id}/certifications`),
        authFetch(`${API}/employees/${id}/documents`),
        authFetch(`${API}/employees/${id}/skills`),
      ]);
      if (emRes.ok) { const d = await emRes.json(); setEmergency(d.contacts || []); }
      if (eduRes.ok) { const d = await eduRes.json(); setEducation(d.education || []); }
      if (certRes.ok) { const d = await certRes.json(); setCerts(d.certifications || []); }
      if (docRes.ok) { const d = await docRes.json(); setDocs(d.documents || []); }
      if (sklRes.ok) { const d = await sklRes.json(); setSkills(d.skills || []); }
      // D111: EEO is RBAC-gated; tolerate 403 silently and fall back to placeholder.
      const [eeoRes, eqRes, accRes] = await Promise.all([
        authFetch(`${API}/employees/${id}/eeo`),
        authFetch(`${API}/employees/${id}/equipment`),
        authFetch(`${API}/section-access`),
      ]);
      if (eeoRes.ok) setEeo(await eeoRes.json());
      else setEeo({ opt_in: false, exists: false });
      if (eqRes.ok) { const d = await eqRes.json(); setEquipment(d.equipment || []); }
      if (accRes.ok) setAccess(await accRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const update = (patch: Partial<Employee>) => setEmp(prev => prev ? { ...prev, ...patch } : prev);

  const savePersonal = async () => {
    if (!emp) return;
    setSaving(true);
    try {
      const payload: any = {
        first_name: emp.first_name, middle_name: emp.middle_name, last_name: emp.last_name,
        preferred_name: emp.preferred_name, display_name: emp.display_name,
        email: emp.email, personal_email: emp.personal_email,
        mobile_phone: emp.mobile_phone, alternate_phone: emp.alternate_phone,
        date_of_birth: emp.date_of_birth || null,
        gender: emp.gender, pronouns: emp.pronouns,
        marital_status: emp.marital_status, nationality: emp.nationality,
        photo_url: emp.photo_url, time_zone: emp.time_zone,
      };
      const r = await authFetch(`${API}/employees/${emp.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
      } else {
        showToast('Personal details saved', true);
        // Persist HOME + MAILING addresses if any field touched
        if (anyAddrField(home)) await saveAddress(home);
        if (anyAddrField(mailing)) await saveAddress(mailing);
        await load();
      }
    } catch (e: any) {
      showToast(e?.message || 'Save failed', false);
    }
    setSaving(false);
  };

  const saveJob = async () => {
    if (!emp) return;
    setSaving(true);
    try {
      const payload: any = {
        entity_code: emp.entity_code, department: emp.department,
        job_title: emp.job_title, job_code: emp.job_code,
        job_family: emp.job_family, job_level: emp.job_level,
        manager_id: emp.manager_id, dotted_line_manager_id: emp.dotted_line_manager_id,
        hire_date: emp.hire_date || null,
        original_hire_date: emp.original_hire_date || null,
        contract_end_date: emp.contract_end_date || null,
        employment_type: emp.employment_type,
        flsa_classification: emp.flsa_classification,
        weekly_hours: emp.weekly_hours, cost_center: emp.cost_center,
        location: emp.location, work_location: emp.work_location,
        remote_status: emp.remote_status,
      };
      const r = await authFetch(`${API}/employees/${emp.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
      } else {
        showToast('Job details saved', true);
        await load();
      }
    } catch (e: any) {
      showToast(e?.message || 'Save failed', false);
    }
    setSaving(false);
  };

  const saveAddress = async (a: Address) => {
    if (!emp) return;
    const payload = {
      address_type: a.address_type,
      street_line1: a.street_line1, street_line2: a.street_line2,
      city: a.city, state_province: a.state_province,
      postal_code: a.postal_code, country: a.country,
      is_primary: !!a.is_primary,
    };
    await authFetch(`${API}/employees/${emp.id}/addresses`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  };

  // ── Banking handlers (D108) ──
  const saveBank = async (acct: BankAccount) => {
    if (!emp) return;
    setSaving(true);
    try {
      const url = acct.id
        ? `${API}/employees/${emp.id}/bank-accounts/${acct.id}`
        : `${API}/employees/${emp.id}/bank-accounts`;
      const r = await authFetch(url, {
        method: acct.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_holder_name: acct.account_holder_name,
          bank_name: acct.bank_name, country: acct.country,
          currency: acct.currency, account_type: acct.account_type,
          payment_method: acct.payment_method,
          account_number: acct.account_number || undefined,
          routing_number: acct.routing_number || undefined,
          iban: acct.iban || undefined, swift: acct.swift || undefined,
          sort_code: acct.sort_code || undefined,
          split_pct: acct.split_pct,
          is_primary: !!acct.is_primary, is_active: acct.is_active !== false,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
      } else {
        showToast('Bank account saved', true);
        setEditBank(null);
        await load();
      }
    } catch (e: any) { showToast(e?.message || 'Save failed', false); }
    setSaving(false);
  };
  const removeBank = async (account_id: number) => {
    if (!emp) return;
    if (!window.confirm('Deactivate this bank account?')) return;
    const r = await authFetch(`${API}/employees/${emp.id}/bank-accounts/${account_id}`, { method: 'DELETE' });
    if (r.ok) { showToast('Bank account deactivated', true); await load(); }
    else showToast('Delete failed', false);
  };
  const revealBank = async (account_id: number) => {
    if (!emp) return;
    const r = await authFetch(`${API}/employees/${emp.id}/bank-accounts/${account_id}/reveal`, { method: 'POST' });
    if (!r.ok) { showToast('Reveal failed', false); return; }
    const data: BankAccount = await r.json();
    setRevealedBank(prev => ({ ...prev, [account_id]: data }));
    setTimeout(() => setRevealedBank(prev => { const n = { ...prev }; delete n[account_id]; return n; }), 15000);
    showToast('Revealed for 15 s (audit-logged)', true);
  };

  // ── Identification handlers (D108) ──
  const saveIdentification = async (ident: Identification) => {
    if (!emp) return;
    setSaving(true);
    try {
      const url = ident.id
        ? `${API}/employees/${emp.id}/identifications/${ident.id}`
        : `${API}/employees/${emp.id}/identifications`;
      const r = await authFetch(url, {
        method: ident.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_type: ident.id_type,
          id_number: ident.id_number || undefined,
          issuing_country: ident.issuing_country,
          issue_date: ident.issue_date || null,
          expiry_date: ident.expiry_date || null,
          doc_url: ident.doc_url, notes: ident.notes,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
      } else {
        showToast('Identification saved', true);
        setEditId(null);
        await load();
      }
    } catch (e: any) { showToast(e?.message || 'Save failed', false); }
    setSaving(false);
  };
  const removeIdentification = async (ident_id: number) => {
    if (!emp) return;
    if (!window.confirm('Delete this identification record?')) return;
    const r = await authFetch(`${API}/employees/${emp.id}/identifications/${ident_id}`, { method: 'DELETE' });
    if (r.ok) { showToast('Identification deleted', true); await load(); }
    else showToast('Delete failed', false);
  };
  const revealIdentification = async (ident_id: number) => {
    if (!emp) return;
    const r = await authFetch(`${API}/employees/${emp.id}/identifications/${ident_id}/reveal`, { method: 'POST' });
    if (!r.ok) { showToast('Reveal failed', false); return; }
    const data: Identification = await r.json();
    setRevealedId(prev => ({ ...prev, [ident_id]: data.id_number || '' }));
    setTimeout(() => setRevealedId(prev => { const n = { ...prev }; delete n[ident_id]; return n; }), 15000);
    showToast('Revealed for 15 s (audit-logged)', true);
  };

  // ── Tax profile handler (D108) ──
  const saveTaxProfile = async () => {
    if (!emp) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API}/employees/${emp.id}/tax-profile`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tax_residency_country: taxProfile.tax_residency_country,
          filing_status: taxProfile.filing_status,
          withholding_allowances: taxProfile.withholding_allowances,
          additional_withholding: taxProfile.additional_withholding,
          state_tax_code: taxProfile.state_tax_code,
          is_exempt: taxProfile.is_exempt, tax_form: taxProfile.tax_form,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
      } else {
        showToast('Tax profile saved', true);
        await load();
      }
    } catch (e: any) { showToast(e?.message || 'Save failed', false); }
    setSaving(false);
  };

  // ── Compensation handlers (D109) ──
  const saveCompSummary = async () => {
    if (!emp) return;
    setSaving(true);
    try {
      const payload: any = {
        annual_salary: emp.annual_salary, currency: emp.currency,
        pay_frequency: (emp as any).pay_frequency, pay_grade: (emp as any).pay_grade,
        bonus_target_pct: (emp as any).bonus_target_pct,
        equity_grant_amount: (emp as any).equity_grant_amount,
        equity_grant_currency: (emp as any).equity_grant_currency,
        last_review_date: (emp as any).last_review_date || null,
        next_review_date: (emp as any).next_review_date || null,
      };
      const r = await authFetch(`${API}/employees/${emp.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
      } else {
        showToast('Compensation saved (history row auto-written if salary/grade changed)', true);
        await load();
      }
    } catch (e: any) { showToast(e?.message || 'Save failed', false); }
    setSaving(false);
  };
  const submitCompChange = async () => {
    if (!emp || !compForm) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API}/employees/${emp.id}/compensation-history`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          effective_date: compForm.effective_date || null,
          change_type: compForm.change_type || 'ADJUSTMENT',
          new_salary: compForm.new_salary,
          new_pay_grade: compForm.new_pay_grade,
          reason: compForm.reason,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
      } else {
        showToast('Comp event logged', true);
        setCompForm(null);
        await load();
      }
    } catch (e: any) { showToast(e?.message || 'Save failed', false); }
    setSaving(false);
  };

  // ── Benefit handlers (D109) ──
  const saveBenefit = async (b: BenefitRow) => {
    if (!emp) return;
    setSaving(true);
    try {
      const url = b.id
        ? `${API}/employees/${emp.id}/benefits/${b.id}`
        : `${API}/employees/${emp.id}/benefits`;
      const r = await authFetch(url, {
        method: b.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(b),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
      } else {
        showToast('Benefit saved', true);
        setEditBenefit(null);
        await load();
      }
    } catch (e: any) { showToast(e?.message || 'Save failed', false); }
    setSaving(false);
  };
  const removeBenefit = async (benefit_id: number) => {
    if (!emp) return;
    if (!window.confirm('Terminate this benefit enrollment?')) return;
    const r = await authFetch(`${API}/employees/${emp.id}/benefits/${benefit_id}`, { method: 'DELETE' });
    if (r.ok) { showToast('Benefit terminated', true); await load(); }
    else showToast('Terminate failed', false);
  };

  // ── Dependent handlers (D109) ──
  const saveDependent = async (d: DependentRow) => {
    if (!emp) return;
    setSaving(true);
    try {
      const url = d.id
        ? `${API}/employees/${emp.id}/dependents/${d.id}`
        : `${API}/employees/${emp.id}/dependents`;
      const r = await authFetch(url, {
        method: d.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...d, ssn: d.ssn || undefined }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
      } else {
        showToast('Dependent saved', true);
        setEditDep(null);
        await load();
      }
    } catch (e: any) { showToast(e?.message || 'Save failed', false); }
    setSaving(false);
  };
  const removeDependent = async (dep_id: number) => {
    if (!emp) return;
    if (!window.confirm('Remove this dependent?')) return;
    const r = await authFetch(`${API}/employees/${emp.id}/dependents/${dep_id}`, { method: 'DELETE' });
    if (r.ok) { showToast('Dependent removed', true); await load(); }
    else showToast('Remove failed', false);
  };

  // ── Generic D110 CRUD helpers ──
  const saveResource = async (path: string, body: any, onSuccess: string) => {
    if (!emp) return;
    setSaving(true);
    try {
      const url = body.id
        ? `${API}/employees/${emp.id}/${path}/${body.id}`
        : `${API}/employees/${emp.id}/${path}`;
      const r = await authFetch(url, {
        method: body.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
        setSaving(false);
        return false;
      }
      showToast(onSuccess, true);
      await load();
    } catch (e: any) {
      showToast(e?.message || 'Save failed', false);
      setSaving(false);
      return false;
    }
    setSaving(false);
    return true;
  };
  const deleteResource = async (path: string, id: number, confirmMsg: string, successMsg: string) => {
    if (!emp) return;
    if (!window.confirm(confirmMsg)) return;
    const r = await authFetch(`${API}/employees/${emp.id}/${path}/${id}`, { method: 'DELETE' });
    if (r.ok) { showToast(successMsg, true); await load(); }
    else showToast('Delete failed', false);
  };
  const ackDocument = async (d: DocRow) => {
    if (!emp || !d.id) return;
    await saveResource('documents', { id: d.id, acknowledged_at: 'now' }, 'Document acknowledged');
  };

  // ── EEO handler (D111) ──
  const saveEeo = async () => {
    if (!emp) return;
    setSaving(true);
    try {
      const r = await authFetch(`${API}/employees/${emp.id}/eeo`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ethnicity: eeo.ethnicity, race: eeo.race,
          veteran_status: eeo.veteran_status,
          disability_status: eeo.disability_status,
          military_status: eeo.military_status,
          eeo_job_category: eeo.eeo_job_category,
          opt_in: !!eeo.opt_in,
          disclosure_date: eeo.disclosure_date || null,
        }),
      });
      if (r.status === 403) showToast('Not permitted to write EEO data for this employee', false);
      else if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        showToast(typeof e.detail === 'string' ? e.detail : 'Save failed', false);
      } else {
        showToast('EEO disclosure saved', true);
        await load();
      }
    } catch (e: any) { showToast(e?.message || 'Save failed', false); }
    setSaving(false);
  };

  if (loading) return <PageLayout title="Loading…"><div /></PageLayout>;
  if (!emp) return (
    <PageLayout title="Employee not found"
      action={{ label: '← Back', onClick: () => navigate('/hr'), variant: 'secondary' }}>
      <div style={{ color: 'var(--text-secondary)' }}>No record for id {id}.</div>
    </PageLayout>
  );

  const fullName = [emp.first_name, emp.middle_name, emp.last_name].filter(Boolean).join(' ');
  const subtitle = [emp.job_title, emp.department, emp.entity_code].filter(Boolean).join(' • ');

  return (
    <PageLayout
      title={emp.display_name || emp.preferred_name || fullName || `Employee ${emp.id}`}
      subtitle={subtitle || undefined}
      actions={[
        { label: '← Back to People', onClick: () => navigate('/hr'), variant: 'secondary' },
      ]}
    >
      {/* Header summary card */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '16px 20px',
        background: '#fff', border: '1px solid var(--border-default)', borderRadius: 8, marginBottom: 20 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', overflow: 'hidden',
          background: 'var(--accent-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, fontWeight: 700, color: 'var(--navy-900)' }}>
          {emp.photo_url
            ? <img src={emp.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : (emp.first_name?.[0] || '') + (emp.last_name?.[0] || '')}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
            {emp.employee_number ? `#${emp.employee_number}` : 'No employee number'}
            {emp.email ? ` • ${emp.email}` : ''}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-hint)', marginTop: 4 }}>
            Hired {emp.hire_date || '—'} • {emp.employment_type || '—'} • {emp.location || '—'}
          </div>
        </div>
        <StatusBadge status={emp.lifecycle_status || 'UNKNOWN'} />
      </div>

      {/* Tabs -- role-aware via D111 RBAC. The server filters writes; this
          just hides tabs the role can't read so the UI stays clean. */}
      <TabBar
        tabs={(() => {
          const allowed = access?.visible_sections;
          return TABS
            .filter(t => !allowed || allowed.includes(t.key))
            .map(t => ({ key: t.key, label: t.enabled ? t.label : `${t.label} ·` }));
        })()}
        active={tab}
        onChange={(k) => {
          const t = TABS.find(x => x.key === k);
          if (t?.enabled) setTab(k);
          else showToast(`${t?.label} tab ships in a later deploy`, false);
        }}
      />

      {tab === 'personal' && (
        <Section>
          <Grid>
            <Field label="First name"><Input value={emp.first_name || ''} onChange={v => update({ first_name: v })} /></Field>
            <Field label="Middle name"><Input value={emp.middle_name || ''} onChange={v => update({ middle_name: v })} /></Field>
            <Field label="Last name"><Input value={emp.last_name || ''} onChange={v => update({ last_name: v })} /></Field>
            <Field label="Preferred name"><Input value={emp.preferred_name || ''} onChange={v => update({ preferred_name: v })} /></Field>
            <Field label="Display name"><Input value={emp.display_name || ''} onChange={v => update({ display_name: v })} /></Field>
            <Field label="Date of birth"><Input type="date" value={emp.date_of_birth || ''} onChange={v => update({ date_of_birth: v })} /></Field>
            <Field label="Gender">
              <Select label="Gender" value={emp.gender || ''} onChange={v => update({ gender: v })}
                options={['', 'FEMALE', 'MALE', 'NON_BINARY', 'PREFER_NOT_TO_SAY']} />
            </Field>
            <Field label="Pronouns"><Input value={emp.pronouns || ''} onChange={v => update({ pronouns: v })} placeholder="she/her, he/him, they/them…" /></Field>
            <Field label="Marital status">
              <Select label="Marital status" value={emp.marital_status || ''} onChange={v => update({ marital_status: v })}
                options={['', 'SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED', 'DOMESTIC_PARTNERSHIP', 'PREFER_NOT_TO_SAY']} />
            </Field>
            <Field label="Nationality"><Input value={emp.nationality || ''} onChange={v => update({ nationality: v })} /></Field>
            <Field label="Photo URL"><Input value={emp.photo_url || ''} onChange={v => update({ photo_url: v })} placeholder="https://… (S3 upload UI in Deploy 5)" /></Field>
            <Field label="Time zone"><Input value={emp.time_zone || ''} onChange={v => update({ time_zone: v })} placeholder="America/New_York" /></Field>
            <Field label="Work email"><Input type="email" value={emp.email || ''} onChange={v => update({ email: v })} /></Field>
            <Field label="Personal email"><Input type="email" value={emp.personal_email || ''} onChange={v => update({ personal_email: v })} /></Field>
            <Field label="Mobile phone"><Input value={emp.mobile_phone || ''} onChange={v => update({ mobile_phone: v })} /></Field>
            <Field label="Alternate phone"><Input value={emp.alternate_phone || ''} onChange={v => update({ alternate_phone: v })} /></Field>
          </Grid>

          <Subhead>Home address</Subhead>
          <AddressForm value={home} onChange={setHome} />

          <Subhead>Mailing address (if different)</Subhead>
          <AddressForm value={mailing} onChange={setMailing} />

          <SaveBar saving={saving} onSave={savePersonal} />
        </Section>
      )}

      {tab === 'job' && (
        <Section>
          <Grid>
            <Field label="Legal entity"><Input value={emp.entity_code || ''} onChange={v => update({ entity_code: v })} /></Field>
            <Field label="Department"><Input value={emp.department || ''} onChange={v => update({ department: v })} /></Field>
            <Field label="Job title"><Input value={emp.job_title || ''} onChange={v => update({ job_title: v })} /></Field>
            <Field label="Job code"><Input value={emp.job_code || ''} onChange={v => update({ job_code: v })} placeholder="e.g. ENG-SR-3" /></Field>
            <Field label="Job family"><Input value={emp.job_family || ''} onChange={v => update({ job_family: v })} placeholder="Engineering, Finance, Sales…" /></Field>
            <Field label="Job level / band"><Input value={emp.job_level || ''} onChange={v => update({ job_level: v })} placeholder="L1–L7" /></Field>
            <Field label="Manager (employee #)"><Input type="number" value={emp.manager_id ?? ''} onChange={v => update({ manager_id: v ? parseInt(v) : null })} /></Field>
            <Field label="Dotted-line manager"><Input type="number" value={emp.dotted_line_manager_id ?? ''} onChange={v => update({ dotted_line_manager_id: v ? parseInt(v) : null })} /></Field>
            <Field label="Employment type">
              <Select label="Employment type" value={emp.employment_type || ''} onChange={v => update({ employment_type: v })}
                options={['', 'FULL_TIME', 'PART_TIME', 'CONTRACTOR', 'INTERN', 'TEMPORARY']} />
            </Field>
            <Field label="FLSA">
              <Select label="FLSA classification" value={emp.flsa_classification || ''} onChange={v => update({ flsa_classification: v })}
                options={['', 'EXEMPT', 'NON_EXEMPT']} />
            </Field>
            <Field label="Weekly hours"><Input type="number" value={emp.weekly_hours ?? ''} onChange={v => update({ weekly_hours: v ? parseFloat(v) : null })} /></Field>
            <Field label="Hire date"><Input type="date" value={emp.hire_date || ''} onChange={v => update({ hire_date: v })} /></Field>
            <Field label="Original hire date"><Input type="date" value={emp.original_hire_date || ''} onChange={v => update({ original_hire_date: v })} /></Field>
            <Field label="Contract end date"><Input type="date" value={emp.contract_end_date || ''} onChange={v => update({ contract_end_date: v })} /></Field>
            <Field label="Cost center"><Input value={emp.cost_center || ''} onChange={v => update({ cost_center: v })} /></Field>
            <Field label="Location (legacy)"><Input value={emp.location || ''} onChange={v => update({ location: v })} /></Field>
            <Field label="Work location"><Input value={emp.work_location || ''} onChange={v => update({ work_location: v })} placeholder="Office, City, Region" /></Field>
            <Field label="Remote status">
              <Select label="Remote status" value={emp.remote_status || ''} onChange={v => update({ remote_status: v })}
                options={['', 'ONSITE', 'HYBRID', 'REMOTE']} />
            </Field>
          </Grid>
          <SaveBar saving={saving} onSave={saveJob} />
        </Section>
      )}

      {tab === 'banking' && (
        <Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Subhead>Bank accounts</Subhead>
            <button onClick={() => setEditBank({ payment_method: 'ACH', account_type: 'CHECKING', is_primary: bankAccounts.length === 0 })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>
              + Add bank account
            </button>
          </div>
          {bankAccounts.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No bank accounts on file. Reimbursements need a primary account before the bank file can be generated.</div>}
          {bankAccounts.map(a => (
            <div key={a.id} style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy-900)' }}>
                    {a.bank_name || 'Bank'}{a.is_primary && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', background: 'var(--accent-subtle)', color: 'var(--navy-900)', borderRadius: 8 }}>PRIMARY</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {a.account_holder_name || '—'} · {a.country || '—'} · {a.currency || '—'} · {a.account_type || '—'} · {a.payment_method || '—'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, fontFamily: 'monospace' }}>
                    {a.id && revealedBank[a.id] ? (
                      <>
                        Account: {revealedBank[a.id].account_number || '—'}
                        {revealedBank[a.id].routing_number && <> · Routing: {revealedBank[a.id].routing_number}</>}
                        {revealedBank[a.id].iban && <> · IBAN: {revealedBank[a.id].iban}</>}
                        {revealedBank[a.id].swift && <> · SWIFT: {revealedBank[a.id].swift}</>}
                        {revealedBank[a.id].sort_code && <> · Sort: {revealedBank[a.id].sort_code}</>}
                      </>
                    ) : (
                      <>
                        Account: ••••{a.account_number_last4 || '----'}
                        {a.has_routing && <> · Routing on file</>}
                        {a.has_iban && <> · IBAN on file</>}
                        {a.has_swift && <> · SWIFT on file</>}
                      </>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {a.id && !revealedBank[a.id] && (
                    <button onClick={() => revealBank(a.id!)}
                      style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Reveal</button>
                  )}
                  <button onClick={() => setEditBank({ ...a })}
                    style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => removeBank(a.id!)}
                    style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', color: '#991b1b', cursor: 'pointer' }}>Deactivate</button>
                </div>
              </div>
            </div>
          ))}
          {editBank && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)' }}>
              <Subhead>{editBank.id ? 'Edit bank account' : 'New bank account'}</Subhead>
              <Grid>
                <Field label="Bank name"><Input value={editBank.bank_name || ''} onChange={v => setEditBank({ ...editBank, bank_name: v })} /></Field>
                <Field label="Account holder"><Input value={editBank.account_holder_name || ''} onChange={v => setEditBank({ ...editBank, account_holder_name: v })} /></Field>
                <Field label="Country"><Input value={editBank.country || ''} onChange={v => setEditBank({ ...editBank, country: v })} /></Field>
                <Field label="Currency"><Input value={editBank.currency || ''} onChange={v => setEditBank({ ...editBank, currency: v })} placeholder="USD, EUR, BRL…" /></Field>
                <Field label="Account type"><Select label="Account type" value={editBank.account_type || ''} onChange={v => setEditBank({ ...editBank, account_type: v })} options={['', 'CHECKING', 'SAVINGS']} /></Field>
                <Field label="Payment method"><Select label="Payment method" value={editBank.payment_method || ''} onChange={v => setEditBank({ ...editBank, payment_method: v })} options={['', 'ACH', 'WIRE', 'CHECK', 'SEPA']} /></Field>
                <Field label="Account number"><Input value={editBank.account_number || ''} onChange={v => setEditBank({ ...editBank, account_number: v })} placeholder={editBank.id ? `(••••${editBank.account_number_last4 || ''} - leave blank to keep)` : ''} /></Field>
                <Field label="Routing number (US)"><Input value={editBank.routing_number || ''} onChange={v => setEditBank({ ...editBank, routing_number: v })} placeholder={editBank.has_routing ? '(stored - leave blank to keep)' : ''} /></Field>
                <Field label="IBAN (intl)"><Input value={editBank.iban || ''} onChange={v => setEditBank({ ...editBank, iban: v })} placeholder={editBank.has_iban ? '(stored - leave blank to keep)' : ''} /></Field>
                <Field label="SWIFT / BIC"><Input value={editBank.swift || ''} onChange={v => setEditBank({ ...editBank, swift: v })} placeholder={editBank.has_swift ? '(stored - leave blank to keep)' : ''} /></Field>
                <Field label="Sort code (UK)"><Input value={editBank.sort_code || ''} onChange={v => setEditBank({ ...editBank, sort_code: v })} placeholder={editBank.has_sort_code ? '(stored - leave blank to keep)' : ''} /></Field>
                <Field label="Split %"><Input type="number" value={editBank.split_pct ?? ''} onChange={v => setEditBank({ ...editBank, split_pct: v ? parseFloat(v) : null })} placeholder="e.g. 100" /></Field>
              </Grid>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={!!editBank.is_primary} onChange={e => setEditBank({ ...editBank, is_primary: e.target.checked })} />
                Primary (this is where reimbursements get sent)
              </label>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditBank(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => saveBank(editBank)} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>
                  {saving ? 'Saving…' : 'Save bank account'}
                </button>
              </div>
            </div>
          )}
        </Section>
      )}

      {tab === 'tax_id' && (
        <Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Subhead>Government IDs</Subhead>
            <button onClick={() => setEditId({ id_type: 'PASSPORT' })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>
              + Add ID
            </button>
          </div>
          {identifications.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No identifications on file.</div>}
          {identifications.map(i => (
            <div key={i.id} style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy-900)' }}>{i.id_type}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, fontFamily: 'monospace' }}>
                    {i.id && revealedId[i.id]
                      ? <>Number: {revealedId[i.id]}</>
                      : <>Number: ••••{i.id_number_last4 || '----'}</>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {i.issuing_country || '—'} · Issued {i.issue_date || '—'} · Expires {i.expiry_date || '—'}
                  </div>
                  {i.notes && <div style={{ fontSize: 12, color: 'var(--text-hint)', marginTop: 4 }}>{i.notes}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {i.id && !revealedId[i.id] && (
                    <button onClick={() => revealIdentification(i.id!)}
                      style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Reveal</button>
                  )}
                  <button onClick={() => setEditId({ ...i })}
                    style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => removeIdentification(i.id!)}
                    style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', color: '#991b1b', cursor: 'pointer' }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
          {editId && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)' }}>
              <Subhead>{editId.id ? 'Edit identification' : 'New identification'}</Subhead>
              <Grid>
                <Field label="ID type">
                  <Select label="ID type" value={editId.id_type || ''} onChange={v => setEditId({ ...editId, id_type: v })}
                    options={['', 'SSN', 'PASSPORT', 'VISA', 'WORK_PERMIT', 'NATIONAL_ID', 'DRIVERS_LICENSE', 'TAX_ID']} />
                </Field>
                <Field label="ID number"><Input value={editId.id_number || ''} onChange={v => setEditId({ ...editId, id_number: v })} placeholder={editId.id ? `(••••${editId.id_number_last4 || ''} - leave blank to keep)` : ''} /></Field>
                <Field label="Issuing country"><Input value={editId.issuing_country || ''} onChange={v => setEditId({ ...editId, issuing_country: v })} /></Field>
                <Field label="Issue date"><Input type="date" value={editId.issue_date || ''} onChange={v => setEditId({ ...editId, issue_date: v })} /></Field>
                <Field label="Expiry date"><Input type="date" value={editId.expiry_date || ''} onChange={v => setEditId({ ...editId, expiry_date: v })} /></Field>
                <Field label="Document URL"><Input value={editId.doc_url || ''} onChange={v => setEditId({ ...editId, doc_url: v })} /></Field>
              </Grid>
              <Field label="Notes"><Input value={editId.notes || ''} onChange={v => setEditId({ ...editId, notes: v })} /></Field>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditId(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => saveIdentification(editId)} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>
                  {saving ? 'Saving…' : 'Save identification'}
                </button>
              </div>
            </div>
          )}

          <Subhead>Tax profile</Subhead>
          <Grid>
            <Field label="Tax residency country"><Input value={taxProfile.tax_residency_country || ''} onChange={v => setTaxProfile({ ...taxProfile, tax_residency_country: v })} /></Field>
            <Field label="Tax form">
              <Select label="Tax form" value={taxProfile.tax_form || ''} onChange={v => setTaxProfile({ ...taxProfile, tax_form: v })}
                options={['', 'W-4', 'W-8BEN', 'W-9', 'W-8BEN-E', 'OTHER']} />
            </Field>
            <Field label="Filing status">
              <Select label="Filing status" value={taxProfile.filing_status || ''} onChange={v => setTaxProfile({ ...taxProfile, filing_status: v })}
                options={['', 'SINGLE', 'MARRIED_JOINT', 'MARRIED_SEPARATE', 'HEAD_OF_HOUSEHOLD']} />
            </Field>
            <Field label="Withholding allowances"><Input type="number" value={taxProfile.withholding_allowances ?? ''} onChange={v => setTaxProfile({ ...taxProfile, withholding_allowances: v ? parseInt(v) : null })} /></Field>
            <Field label="Additional withholding"><Input type="number" value={taxProfile.additional_withholding ?? ''} onChange={v => setTaxProfile({ ...taxProfile, additional_withholding: v ? parseFloat(v) : null })} /></Field>
            <Field label="State tax code"><Input value={taxProfile.state_tax_code || ''} onChange={v => setTaxProfile({ ...taxProfile, state_tax_code: v })} placeholder="CA, NY, TX…" /></Field>
          </Grid>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={!!taxProfile.is_exempt} onChange={e => setTaxProfile({ ...taxProfile, is_exempt: e.target.checked })} />
            Tax-exempt
          </label>
          <SaveBar saving={saving} onSave={saveTaxProfile} />
        </Section>
      )}

      {tab === 'compensation' && (
        <Section>
          <Subhead>Compensation summary</Subhead>
          <Grid>
            <Field label="Annual salary"><Input type="number" value={emp.annual_salary ?? ''} onChange={v => update({ annual_salary: v ? parseFloat(v) : null })} /></Field>
            <Field label="Currency"><Input value={emp.currency || ''} onChange={v => update({ currency: v })} placeholder="USD, EUR, BRL…" /></Field>
            <Field label="Pay frequency">
              <Select label="Pay frequency" value={(emp as any).pay_frequency || ''} onChange={v => update({ pay_frequency: v } as any)}
                options={['', 'WEEKLY', 'BIWEEKLY', 'SEMIMONTHLY', 'MONTHLY']} />
            </Field>
            <Field label="Pay grade"><Input value={(emp as any).pay_grade || ''} onChange={v => update({ pay_grade: v } as any)} placeholder="G1–G15, B1–B7…" /></Field>
            <Field label="Bonus target %"><Input type="number" value={(emp as any).bonus_target_pct ?? ''} onChange={v => update({ bonus_target_pct: v ? parseFloat(v) : null } as any)} /></Field>
            <Field label="Equity grant amount"><Input type="number" value={(emp as any).equity_grant_amount ?? ''} onChange={v => update({ equity_grant_amount: v ? parseFloat(v) : null } as any)} /></Field>
            <Field label="Equity currency"><Input value={(emp as any).equity_grant_currency || ''} onChange={v => update({ equity_grant_currency: v } as any)} /></Field>
            <Field label="Last review date"><Input type="date" value={(emp as any).last_review_date || ''} onChange={v => update({ last_review_date: v } as any)} /></Field>
            <Field label="Next review date"><Input type="date" value={(emp as any).next_review_date || ''} onChange={v => update({ next_review_date: v } as any)} /></Field>
          </Grid>
          <SaveBar saving={saving} onSave={saveCompSummary} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, marginBottom: 12 }}>
            <Subhead>Compensation history</Subhead>
            <button onClick={() => setCompForm({ change_type: 'BONUS_AWARD', effective_date: new Date().toISOString().slice(0, 10) })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>
              + Add comp event
            </button>
          </div>
          {compForm && (
            <div style={{ padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)', marginBottom: 16 }}>
              <Grid>
                <Field label="Effective date"><Input type="date" value={compForm.effective_date || ''} onChange={v => setCompForm({ ...compForm, effective_date: v })} /></Field>
                <Field label="Change type">
                  <Select label="Change type" value={compForm.change_type || ''} onChange={v => setCompForm({ ...compForm, change_type: v })}
                    options={['', 'HIRE', 'RAISE', 'PROMOTION', 'DEMOTION', 'ADJUSTMENT', 'OFF_CYCLE', 'EQUITY_GRANT', 'BONUS_AWARD']} />
                </Field>
                <Field label="New salary"><Input type="number" value={compForm.new_salary ?? ''} onChange={v => setCompForm({ ...compForm, new_salary: v ? parseFloat(v) : null })} /></Field>
                <Field label="New pay grade"><Input value={compForm.new_pay_grade || ''} onChange={v => setCompForm({ ...compForm, new_pay_grade: v })} /></Field>
              </Grid>
              <Field label="Reason"><Input value={compForm.reason || ''} onChange={v => setCompForm({ ...compForm, reason: v })} placeholder="Promotion to Sr, market adjustment, board-approved equity grant…" /></Field>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setCompForm(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={submitCompChange} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>
                  {saving ? 'Saving…' : 'Log event'}
                </button>
              </div>
            </div>
          )}
          {compHistory.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No compensation history yet.</div>}
          {compHistory.map(r => (
            <div key={r.id} style={{ display: 'flex', gap: 14, padding: 12, borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ width: 90, fontSize: 12, color: 'var(--text-secondary)' }}>{r.effective_date}</div>
              <div style={{ width: 110, fontSize: 11, fontWeight: 700, color: 'var(--navy-900)', padding: '2px 8px', background: 'var(--accent-subtle)', borderRadius: 4, height: 18, alignSelf: 'flex-start' }}>{r.change_type}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, color: 'var(--navy-900)' }}>
                  {r.previous_salary != null && r.new_salary != null
                    ? <>{r.currency || ''} {r.previous_salary.toLocaleString()} → <strong>{r.new_salary.toLocaleString()}</strong>{r.change_pct != null && <span style={{ marginLeft: 8, color: r.change_pct >= 0 ? '#15803d' : '#991b1b', fontSize: 12 }}>({r.change_pct >= 0 ? '+' : ''}{r.change_pct}%)</span>}</>
                    : r.new_salary != null ? <>{r.currency || ''} {r.new_salary.toLocaleString()}</> : '—'}
                </div>
                {(r.previous_pay_grade || r.new_pay_grade) && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                    Grade: {r.previous_pay_grade || '—'} → {r.new_pay_grade || '—'}
                  </div>
                )}
                {r.reason && <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 2 }}>{r.reason}</div>}
              </div>
            </div>
          ))}
        </Section>
      )}

      {tab === 'emergency' && (
        <Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Subhead>Emergency contacts</Subhead>
            <button onClick={() => setEditEmerg({ name: '', relationship: 'SPOUSE', is_primary: emergency.length === 0, priority_order: emergency.length + 1 })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>+ Add contact</button>
          </div>
          {emergency.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No emergency contacts on file.</div>}
          {emergency.map(c => (
            <div key={c.id} style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy-900)' }}>
                    {c.name}{c.is_primary && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', background: 'var(--accent-subtle)', color: 'var(--navy-900)', borderRadius: 8 }}>PRIMARY</span>}
                    <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-secondary)', fontWeight: 400 }}>· {c.relationship || '—'}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {c.primary_phone || '—'}{c.alt_phone && <> / {c.alt_phone}</>} · {c.email || '—'}
                  </div>
                  {c.address && <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>{c.address}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditEmerg({ ...c })} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => deleteResource('emergency-contacts', c.id!, 'Remove this emergency contact?', 'Contact removed')} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', color: '#991b1b', cursor: 'pointer' }}>Remove</button>
                </div>
              </div>
            </div>
          ))}
          {editEmerg && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)' }}>
              <Subhead>{editEmerg.id ? 'Edit emergency contact' : 'New emergency contact'}</Subhead>
              <Grid>
                <Field label="Name"><Input value={editEmerg.name || ''} onChange={v => setEditEmerg({ ...editEmerg, name: v })} /></Field>
                <Field label="Relationship">
                  <Select label="Relationship" value={editEmerg.relationship || ''} onChange={v => setEditEmerg({ ...editEmerg, relationship: v })}
                    options={['', 'SPOUSE', 'DOMESTIC_PARTNER', 'PARENT', 'SIBLING', 'CHILD', 'FRIEND', 'NEIGHBOR', 'OTHER']} />
                </Field>
                <Field label="Primary phone"><Input value={editEmerg.primary_phone || ''} onChange={v => setEditEmerg({ ...editEmerg, primary_phone: v })} /></Field>
                <Field label="Alt phone"><Input value={editEmerg.alt_phone || ''} onChange={v => setEditEmerg({ ...editEmerg, alt_phone: v })} /></Field>
                <Field label="Email"><Input type="email" value={editEmerg.email || ''} onChange={v => setEditEmerg({ ...editEmerg, email: v })} /></Field>
                <Field label="Priority order"><Input type="number" value={editEmerg.priority_order ?? ''} onChange={v => setEditEmerg({ ...editEmerg, priority_order: v ? parseInt(v) : undefined })} /></Field>
              </Grid>
              <Field label="Address"><Input value={editEmerg.address || ''} onChange={v => setEditEmerg({ ...editEmerg, address: v })} /></Field>
              <Field label="Notes"><Input value={editEmerg.notes || ''} onChange={v => setEditEmerg({ ...editEmerg, notes: v })} /></Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={!!editEmerg.is_primary} onChange={e => setEditEmerg({ ...editEmerg, is_primary: e.target.checked })} />
                Primary contact
              </label>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditEmerg(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={async () => { const ok = await saveResource('emergency-contacts', editEmerg, 'Emergency contact saved'); if (ok) setEditEmerg(null); }} disabled={saving}
                  style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          )}
        </Section>
      )}

      {tab === 'education' && (
        <Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Subhead>Education</Subhead>
            <button onClick={() => setEditEdu({ degree_type: 'BACHELORS' })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>+ Add degree</button>
          </div>
          {education.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No education on file.</div>}
          {education.map(e => (
            <div key={e.id} style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy-900)' }}>
                    {e.degree_type || '—'} · {e.field_of_study || '—'}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {e.institution || '—'} · {e.country || '—'} · {e.graduation_year || '—'}{e.gpa != null && <> · GPA {e.gpa}</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditEdu({ ...e })} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => deleteResource('education', e.id!, 'Remove this education record?', 'Removed')} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', color: '#991b1b', cursor: 'pointer' }}>Remove</button>
                </div>
              </div>
            </div>
          ))}
          {editEdu && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)' }}>
              <Subhead>{editEdu.id ? 'Edit education' : 'New education'}</Subhead>
              <Grid>
                <Field label="Institution"><Input value={editEdu.institution || ''} onChange={v => setEditEdu({ ...editEdu, institution: v })} /></Field>
                <Field label="Degree type">
                  <Select label="Degree type" value={editEdu.degree_type || ''} onChange={v => setEditEdu({ ...editEdu, degree_type: v })}
                    options={['', 'HIGH_SCHOOL', 'ASSOCIATES', 'BACHELORS', 'MASTERS', 'PHD', 'CERTIFICATE', 'DIPLOMA', 'OTHER']} />
                </Field>
                <Field label="Field of study"><Input value={editEdu.field_of_study || ''} onChange={v => setEditEdu({ ...editEdu, field_of_study: v })} /></Field>
                <Field label="Graduation year"><Input type="number" value={editEdu.graduation_year ?? ''} onChange={v => setEditEdu({ ...editEdu, graduation_year: v ? parseInt(v) : null })} /></Field>
                <Field label="GPA"><Input type="number" value={editEdu.gpa ?? ''} onChange={v => setEditEdu({ ...editEdu, gpa: v ? parseFloat(v) : null })} /></Field>
                <Field label="Country"><Input value={editEdu.country || ''} onChange={v => setEditEdu({ ...editEdu, country: v })} /></Field>
                <Field label="Diploma URL"><Input value={editEdu.doc_url || ''} onChange={v => setEditEdu({ ...editEdu, doc_url: v })} /></Field>
              </Grid>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditEdu(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={async () => { const ok = await saveResource('education', editEdu, 'Education saved'); if (ok) setEditEdu(null); }} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, marginBottom: 12 }}>
            <Subhead>Certifications</Subhead>
            <button onClick={() => setEditCert({ certification_name: '', is_renewable: true, status: 'ACTIVE' })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>+ Add certification</button>
          </div>
          {certs.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No certifications on file.</div>}
          {certs.map(c => (
            <div key={c.id} style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy-900)' }}>
                    {c.certification_name}
                    {c.effective_status === 'EXPIRED' && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', background: '#fee2e2', color: '#991b1b', borderRadius: 8 }}>EXPIRED</span>}
                    {c.effective_status === 'PENDING_RENEWAL' && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 8 }}>RENEWAL DUE</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {c.issuer || '—'}{c.certification_number && <> · #{c.certification_number}</>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>
                    Issued {c.issue_date || '—'} · Expires {c.expiry_date || '—'}
                    {c.days_to_expiry != null && c.days_to_expiry >= 0 && <> ({c.days_to_expiry} d)</>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditCert({ ...c })} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => deleteResource('certifications', c.id!, 'Remove this certification?', 'Removed')} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', color: '#991b1b', cursor: 'pointer' }}>Remove</button>
                </div>
              </div>
            </div>
          ))}
          {editCert && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)' }}>
              <Subhead>{editCert.id ? 'Edit certification' : 'New certification'}</Subhead>
              <Grid>
                <Field label="Certification"><Input value={editCert.certification_name || ''} onChange={v => setEditCert({ ...editCert, certification_name: v })} placeholder="CPA, PMP, AWS Solutions Architect…" /></Field>
                <Field label="Issuer"><Input value={editCert.issuer || ''} onChange={v => setEditCert({ ...editCert, issuer: v })} /></Field>
                <Field label="Cert #"><Input value={editCert.certification_number || ''} onChange={v => setEditCert({ ...editCert, certification_number: v })} /></Field>
                <Field label="Issue date"><Input type="date" value={editCert.issue_date || ''} onChange={v => setEditCert({ ...editCert, issue_date: v })} /></Field>
                <Field label="Expiry date"><Input type="date" value={editCert.expiry_date || ''} onChange={v => setEditCert({ ...editCert, expiry_date: v })} /></Field>
                <Field label="Status">
                  <Select label="Status" value={editCert.status || ''} onChange={v => setEditCert({ ...editCert, status: v })}
                    options={['', 'ACTIVE', 'PENDING_RENEWAL', 'EXPIRED', 'REVOKED']} />
                </Field>
                <Field label="Doc URL"><Input value={editCert.doc_url || ''} onChange={v => setEditCert({ ...editCert, doc_url: v })} /></Field>
              </Grid>
              <Field label="Notes"><Input value={editCert.notes || ''} onChange={v => setEditCert({ ...editCert, notes: v })} /></Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={editCert.is_renewable !== false} onChange={e => setEditCert({ ...editCert, is_renewable: e.target.checked })} />Renewable
              </label>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditCert(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={async () => { const ok = await saveResource('certifications', editCert, 'Certification saved'); if (ok) setEditCert(null); }} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, marginBottom: 12 }}>
            <Subhead>Skills</Subhead>
            <button onClick={() => setEditSkill({ skill_name: '', proficiency_level: 'INTERMEDIATE' })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>+ Add skill</button>
          </div>
          {skills.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No skills on file.</div>}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {skills.map(s => (
              <div key={s.id} style={{ padding: '6px 12px', borderRadius: 18, border: s.is_primary ? '1px solid var(--navy-900)' : '1px solid var(--border-default)', background: s.is_primary ? 'var(--accent-subtle)' : '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy-900)' }}>{s.skill_name}</span>
                {s.proficiency_level && <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>· {s.proficiency_level}</span>}
                {s.years_experience != null && <span style={{ fontSize: 10, color: 'var(--text-hint)' }}>· {s.years_experience}y</span>}
                <button onClick={() => setEditSkill({ ...s })} style={{ padding: '0 4px', fontSize: 10, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer' }}>edit</button>
                <button onClick={() => deleteResource('skills', s.id!, `Remove "${s.skill_name}"?`, 'Removed')} style={{ padding: '0 4px', fontSize: 10, color: '#991b1b', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
              </div>
            ))}
          </div>
          {editSkill && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)' }}>
              <Subhead>{editSkill.id ? 'Edit skill' : 'New skill'}</Subhead>
              <Grid>
                <Field label="Skill"><Input value={editSkill.skill_name || ''} onChange={v => setEditSkill({ ...editSkill, skill_name: v })} placeholder="Python, Excel, leadership, ASC 606…" /></Field>
                <Field label="Proficiency">
                  <Select label="Proficiency" value={editSkill.proficiency_level || ''} onChange={v => setEditSkill({ ...editSkill, proficiency_level: v })}
                    options={['', 'BEGINNER', 'INTERMEDIATE', 'ADVANCED', 'EXPERT']} />
                </Field>
                <Field label="Years"><Input type="number" value={editSkill.years_experience ?? ''} onChange={v => setEditSkill({ ...editSkill, years_experience: v ? parseFloat(v) : null })} /></Field>
              </Grid>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={!!editSkill.is_primary} onChange={e => setEditSkill({ ...editSkill, is_primary: e.target.checked })} />Primary skill
              </label>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditSkill(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={async () => { const ok = await saveResource('skills', editSkill, 'Skill saved'); if (ok) setEditSkill(null); }} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          )}
        </Section>
      )}

      {tab === 'eeo' && (
        <Section>
          <div style={{ marginBottom: 16, padding: 12, background: '#f5f6f8', borderRadius: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
            <strong>Voluntary disclosure.</strong> EEO data is collected for federal compliance reporting (EEO-1) and is treated separately from other HR data. Self-disclosure is voluntary; choosing not to disclose has no impact on employment.
          </div>
          <Subhead>EEO disclosure</Subhead>
          <Grid>
            <Field label="Ethnicity">
              <Select label="Ethnicity" value={eeo.ethnicity || ''} onChange={v => setEeo({ ...eeo, ethnicity: v })}
                options={['', 'HISPANIC_OR_LATINO', 'NOT_HISPANIC_OR_LATINO', 'PREFER_NOT_TO_DISCLOSE']} />
            </Field>
            <Field label="Race">
              <Select label="Race" value={eeo.race || ''} onChange={v => setEeo({ ...eeo, race: v })}
                options={['', 'WHITE', 'BLACK_OR_AFRICAN_AMERICAN', 'ASIAN', 'AMERICAN_INDIAN_ALASKA_NATIVE', 'NATIVE_HAWAIIAN_PACIFIC_ISLANDER', 'TWO_OR_MORE', 'PREFER_NOT_TO_DISCLOSE']} />
            </Field>
            <Field label="Veteran status">
              <Select label="Veteran status" value={eeo.veteran_status || ''} onChange={v => setEeo({ ...eeo, veteran_status: v })}
                options={['', 'VETERAN', 'NOT_A_VETERAN', 'DISABLED_VETERAN', 'RECENTLY_SEPARATED', 'PROTECTED_VETERAN', 'PREFER_NOT_TO_DISCLOSE']} />
            </Field>
            <Field label="Disability status">
              <Select label="Disability status" value={eeo.disability_status || ''} onChange={v => setEeo({ ...eeo, disability_status: v })}
                options={['', 'YES', 'NO', 'PREFER_NOT_TO_DISCLOSE']} />
            </Field>
            <Field label="Military status">
              <Select label="Military status" value={eeo.military_status || ''} onChange={v => setEeo({ ...eeo, military_status: v })}
                options={['', 'ACTIVE', 'RESERVE', 'NATIONAL_GUARD', 'NONE']} />
            </Field>
            <Field label="EEO job category">
              <Select label="EEO job category" value={eeo.eeo_job_category || ''} onChange={v => setEeo({ ...eeo, eeo_job_category: v })}
                options={['', 'EXECUTIVE', 'FIRST_MID_LEVEL_OFFICIAL', 'PROFESSIONAL', 'TECHNICIAN', 'SALES_WORKER', 'ADMINISTRATIVE', 'CRAFT_WORKER', 'OPERATIVE', 'LABORER', 'SERVICE_WORKER']} />
            </Field>
            <Field label="Disclosure date"><Input type="date" value={eeo.disclosure_date || ''} onChange={v => setEeo({ ...eeo, disclosure_date: v })} /></Field>
          </Grid>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 12, color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={!!eeo.opt_in} onChange={e => setEeo({ ...eeo, opt_in: e.target.checked })} />
            <span>I voluntarily disclose the above EEO information for federal reporting purposes.</span>
          </label>
          <SaveBar saving={saving} onSave={saveEeo} />
        </Section>
      )}

      {tab === 'equipment' && (
        <Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Subhead>Assigned equipment</Subhead>
            <button onClick={() => setEditEquip({ equipment_type: 'LAPTOP', condition: 'NEW', is_active: true, assigned_date: new Date().toISOString().slice(0, 10) })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>+ Assign equipment</button>
          </div>
          {equipment.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No equipment assigned.</div>}
          {equipment.map(e => (
            <div key={e.id} style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy-900)' }}>
                    {e.equipment_type}{e.make && <> · {e.make}</>}{e.model && <> {e.model}</>}
                    {!e.is_active && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', background: '#e5e7eb', color: '#374151', borderRadius: 8 }}>RETURNED</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    SN {e.serial_number || '—'}{e.asset_tag && <> · Tag {e.asset_tag}</>} · {e.condition || '—'}
                    {e.estimated_value != null && <> · {e.currency || ''} {e.estimated_value.toLocaleString()}</>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>
                    Assigned {e.assigned_date || '—'}{e.returned_date && <> · Returned {e.returned_date}</>}
                  </div>
                  {e.notes && <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>{e.notes}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditEquip({ ...e })} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Edit</button>
                  {e.is_active && (
                    <button onClick={() => deleteResource('equipment', e.id!, 'Mark this equipment as returned?', 'Equipment marked returned')} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', color: '#991b1b', cursor: 'pointer' }}>Mark returned</button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {editEquip && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)' }}>
              <Subhead>{editEquip.id ? 'Edit equipment' : 'Assign equipment'}</Subhead>
              <Grid>
                <Field label="Type">
                  <Select label="Equipment type" value={editEquip.equipment_type || ''} onChange={v => setEditEquip({ ...editEquip, equipment_type: v })}
                    options={['', 'LAPTOP', 'DESKTOP', 'PHONE', 'MONITOR', 'HEADSET', 'KEYCARD', 'TABLET', 'DOCK', 'KEYBOARD', 'MOUSE', 'OTHER']} />
                </Field>
                <Field label="Make"><Input value={editEquip.make || ''} onChange={v => setEditEquip({ ...editEquip, make: v })} placeholder="Apple, Dell, Logitech…" /></Field>
                <Field label="Model"><Input value={editEquip.model || ''} onChange={v => setEditEquip({ ...editEquip, model: v })} /></Field>
                <Field label="Serial #"><Input value={editEquip.serial_number || ''} onChange={v => setEditEquip({ ...editEquip, serial_number: v })} /></Field>
                <Field label="Asset tag"><Input value={editEquip.asset_tag || ''} onChange={v => setEditEquip({ ...editEquip, asset_tag: v })} /></Field>
                <Field label="Assigned date"><Input type="date" value={editEquip.assigned_date || ''} onChange={v => setEditEquip({ ...editEquip, assigned_date: v })} /></Field>
                <Field label="Returned date"><Input type="date" value={editEquip.returned_date || ''} onChange={v => setEditEquip({ ...editEquip, returned_date: v })} /></Field>
                <Field label="Condition">
                  <Select label="Condition" value={editEquip.condition || ''} onChange={v => setEditEquip({ ...editEquip, condition: v })}
                    options={['', 'NEW', 'GOOD', 'FAIR', 'DAMAGED', 'LOST']} />
                </Field>
                <Field label="Estimated value"><Input type="number" value={editEquip.estimated_value ?? ''} onChange={v => setEditEquip({ ...editEquip, estimated_value: v ? parseFloat(v) : null })} /></Field>
                <Field label="Currency"><Input value={editEquip.currency || ''} onChange={v => setEditEquip({ ...editEquip, currency: v })} /></Field>
                <Field label="Doc URL"><Input value={editEquip.doc_url || ''} onChange={v => setEditEquip({ ...editEquip, doc_url: v })} placeholder="receipt / loaner agreement" /></Field>
              </Grid>
              <Field label="Notes"><Input value={editEquip.notes || ''} onChange={v => setEditEquip({ ...editEquip, notes: v })} /></Field>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditEquip(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={async () => { const ok = await saveResource('equipment', editEquip, 'Equipment saved'); if (ok) setEditEquip(null); }} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          )}
        </Section>
      )}

      {tab === 'documents' && (
        <Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Subhead>Compliance documents</Subhead>
            <button onClick={() => setEditDoc({ document_type: 'I9', status: 'DRAFT' })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>+ Add document</button>
          </div>
          {docs.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No documents on file.</div>}
          {docs.map(d => (
            <div key={d.id} style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy-900)' }}>
                    {d.document_type}{d.document_name && <> · {d.document_name}</>}
                    {d.effective_status === 'EXPIRED' && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', background: '#fee2e2', color: '#991b1b', borderRadius: 8 }}>EXPIRED</span>}
                    {d.signed_by_employee && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', background: '#d1fae5', color: '#065f46', borderRadius: 8 }}>SIGNED</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {d.file_name || '—'} · Status {d.status || 'DRAFT'} · Signed {d.signed_date || '—'} · Expires {d.expiry_date || '—'}
                    {d.days_to_expiry != null && d.days_to_expiry >= 0 && <> ({d.days_to_expiry} d)</>}
                  </div>
                  {d.doc_url && <div style={{ fontSize: 11, marginTop: 4 }}><a href={d.doc_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--navy-900)' }}>View document</a></div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {!d.signed_by_employee && (
                    <button onClick={() => ackDocument(d)} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', color: '#065f46', cursor: 'pointer' }}>Acknowledge</button>
                  )}
                  <button onClick={() => setEditDoc({ ...d })} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => deleteResource('documents', d.id!, 'Delete this document record?', 'Document deleted')} style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', color: '#991b1b', cursor: 'pointer' }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
          {editDoc && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)' }}>
              <Subhead>{editDoc.id ? 'Edit document' : 'New document'}</Subhead>
              <Grid>
                <Field label="Document type">
                  <Select label="Document type" value={editDoc.document_type || ''} onChange={v => setEditDoc({ ...editDoc, document_type: v })}
                    options={['', 'I9', 'NDA', 'OFFER_LETTER', 'HANDBOOK_ACK', 'W4', 'W8BEN', 'W9', 'EMPLOYMENT_CONTRACT', 'SEPARATION', 'BACKGROUND_CHECK', 'PERFORMANCE_REVIEW', 'OTHER']} />
                </Field>
                <Field label="Document name"><Input value={editDoc.document_name || ''} onChange={v => setEditDoc({ ...editDoc, document_name: v })} /></Field>
                <Field label="File name"><Input value={editDoc.file_name || ''} onChange={v => setEditDoc({ ...editDoc, file_name: v })} /></Field>
                <Field label="Doc URL"><Input value={editDoc.doc_url || ''} onChange={v => setEditDoc({ ...editDoc, doc_url: v })} placeholder="S3 URL (S3 upload UI in Deploy 5)" /></Field>
                <Field label="Signed date"><Input type="date" value={editDoc.signed_date || ''} onChange={v => setEditDoc({ ...editDoc, signed_date: v })} /></Field>
                <Field label="Expiry date"><Input type="date" value={editDoc.expiry_date || ''} onChange={v => setEditDoc({ ...editDoc, expiry_date: v })} /></Field>
                <Field label="Status">
                  <Select label="Status" value={editDoc.status || ''} onChange={v => setEditDoc({ ...editDoc, status: v })}
                    options={['', 'DRAFT', 'SENT', 'SIGNED', 'ACKNOWLEDGED', 'EXPIRED', 'VOID']} />
                </Field>
              </Grid>
              <Field label="Notes"><Input value={editDoc.notes || ''} onChange={v => setEditDoc({ ...editDoc, notes: v })} /></Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={!!editDoc.signed_by_employee} onChange={e => setEditDoc({ ...editDoc, signed_by_employee: e.target.checked })} />Signed by employee
              </label>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditDoc(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={async () => { const ok = await saveResource('documents', editDoc, 'Document saved'); if (ok) setEditDoc(null); }} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </div>
          )}
        </Section>
      )}

      {tab === 'benefits' && (
        <Section>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Subhead>Benefit enrollments</Subhead>
            <button onClick={() => setEditBenefit({ benefit_type: 'HEALTH', status: 'ACTIVE', coverage_tier: 'EMPLOYEE', contribution_frequency: 'PER_PAYCHECK' })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>
              + Add benefit
            </button>
          </div>
          {benefits.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No benefit enrollments on file.</div>}
          {benefits.map(b => (
            <div key={b.id} style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy-900)' }}>
                    {b.benefit_type}{b.status && b.status !== 'ACTIVE' && <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 8px', background: '#fee2e2', color: '#991b1b', borderRadius: 8 }}>{b.status}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    {b.plan_name || '—'} · {b.carrier || '—'} · {b.coverage_tier || '—'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>
                    Effective {b.effective_date || '—'}{b.end_date && <> · Ends {b.end_date}</>}
                    {(b.employee_contribution != null || b.employer_contribution != null) && (
                      <> · EE {b.employee_contribution ?? '—'} / ER {b.employer_contribution ?? '—'} {b.currency || ''} {b.contribution_frequency || ''}</>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditBenefit({ ...b })}
                    style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Edit</button>
                  {b.status === 'ACTIVE' && (
                    <button onClick={() => removeBenefit(b.id!)}
                      style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', color: '#991b1b', cursor: 'pointer' }}>Terminate</button>
                  )}
                </div>
              </div>
            </div>
          ))}
          {editBenefit && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)' }}>
              <Subhead>{editBenefit.id ? 'Edit benefit' : 'New benefit'}</Subhead>
              <Grid>
                <Field label="Benefit type">
                  <Select label="Benefit type" value={editBenefit.benefit_type || ''} onChange={v => setEditBenefit({ ...editBenefit, benefit_type: v })}
                    options={['', 'HEALTH', 'DENTAL', 'VISION', '401K', 'PENSION', 'LIFE', 'DISABILITY', 'HSA', 'FSA', 'COMMUTER', 'OTHER']} />
                </Field>
                <Field label="Plan name"><Input value={editBenefit.plan_name || ''} onChange={v => setEditBenefit({ ...editBenefit, plan_name: v })} /></Field>
                <Field label="Carrier"><Input value={editBenefit.carrier || ''} onChange={v => setEditBenefit({ ...editBenefit, carrier: v })} /></Field>
                <Field label="Policy #"><Input value={editBenefit.policy_number || ''} onChange={v => setEditBenefit({ ...editBenefit, policy_number: v })} /></Field>
                <Field label="Coverage tier">
                  <Select label="Coverage tier" value={editBenefit.coverage_tier || ''} onChange={v => setEditBenefit({ ...editBenefit, coverage_tier: v })}
                    options={['', 'EMPLOYEE', 'EE_SPOUSE', 'EE_CHILDREN', 'FAMILY', 'WAIVED']} />
                </Field>
                <Field label="Status">
                  <Select label="Status" value={editBenefit.status || ''} onChange={v => setEditBenefit({ ...editBenefit, status: v })}
                    options={['', 'ACTIVE', 'ENROLLED', 'WAIVED', 'PENDING', 'TERMINATED']} />
                </Field>
                <Field label="Enrollment date"><Input type="date" value={editBenefit.enrollment_date || ''} onChange={v => setEditBenefit({ ...editBenefit, enrollment_date: v })} /></Field>
                <Field label="Effective date"><Input type="date" value={editBenefit.effective_date || ''} onChange={v => setEditBenefit({ ...editBenefit, effective_date: v })} /></Field>
                <Field label="End date"><Input type="date" value={editBenefit.end_date || ''} onChange={v => setEditBenefit({ ...editBenefit, end_date: v })} /></Field>
                <Field label="EE contribution"><Input type="number" value={editBenefit.employee_contribution ?? ''} onChange={v => setEditBenefit({ ...editBenefit, employee_contribution: v ? parseFloat(v) : null })} /></Field>
                <Field label="ER contribution"><Input type="number" value={editBenefit.employer_contribution ?? ''} onChange={v => setEditBenefit({ ...editBenefit, employer_contribution: v ? parseFloat(v) : null })} /></Field>
                <Field label="Frequency">
                  <Select label="Contribution frequency" value={editBenefit.contribution_frequency || ''} onChange={v => setEditBenefit({ ...editBenefit, contribution_frequency: v })}
                    options={['', 'PER_PAYCHECK', 'MONTHLY', 'ANNUAL']} />
                </Field>
                <Field label="Currency"><Input value={editBenefit.currency || ''} onChange={v => setEditBenefit({ ...editBenefit, currency: v })} placeholder="USD" /></Field>
              </Grid>
              <Field label="Notes"><Input value={editBenefit.notes || ''} onChange={v => setEditBenefit({ ...editBenefit, notes: v })} /></Field>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditBenefit(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => saveBenefit(editBenefit)} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>
                  {saving ? 'Saving…' : 'Save benefit'}
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, marginBottom: 12 }}>
            <Subhead>Dependents</Subhead>
            <button onClick={() => setEditDep({ relationship: 'SPOUSE', is_active: true })}
              style={{ padding: '6px 14px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: '#1e3a5f', color: '#fff', cursor: 'pointer' }}>
              + Add dependent
            </button>
          </div>
          {dependents.length === 0 && <div style={{ fontSize: 13, color: 'var(--text-hint)', padding: '12px 0' }}>No dependents on file.</div>}
          {dependents.map(d => (
            <div key={d.id} style={{ border: '1px solid var(--border-default)', borderRadius: 6, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--navy-900)' }}>
                    {d.first_name} {d.last_name} <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 400 }}>· {d.relationship}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                    DOB {d.date_of_birth || '—'}{d.gender && <> · {d.gender}</>}{d.ssn_last4 && <> · SSN ••••{d.ssn_last4}</>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 4 }}>
                    Coverage: {[d.is_covered_health && 'Health', d.is_covered_dental && 'Dental', d.is_covered_vision && 'Vision', d.is_covered_life && 'Life'].filter(Boolean).join(', ') || 'none'}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditDep({ ...d })}
                    style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Edit</button>
                  <button onClick={() => removeDependent(d.id!)}
                    style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: '1px solid var(--border-default)', background: '#fff', color: '#991b1b', cursor: 'pointer' }}>Remove</button>
                </div>
              </div>
            </div>
          ))}
          {editDep && (
            <div style={{ marginTop: 16, padding: 16, border: '1px solid var(--border-default)', borderRadius: 8, background: 'var(--accent-subtle)' }}>
              <Subhead>{editDep.id ? 'Edit dependent' : 'New dependent'}</Subhead>
              <Grid>
                <Field label="First name"><Input value={editDep.first_name || ''} onChange={v => setEditDep({ ...editDep, first_name: v })} /></Field>
                <Field label="Last name"><Input value={editDep.last_name || ''} onChange={v => setEditDep({ ...editDep, last_name: v })} /></Field>
                <Field label="Relationship">
                  <Select label="Relationship" value={editDep.relationship || ''} onChange={v => setEditDep({ ...editDep, relationship: v })}
                    options={['', 'SPOUSE', 'DOMESTIC_PARTNER', 'CHILD', 'STEPCHILD', 'OTHER']} />
                </Field>
                <Field label="Date of birth"><Input type="date" value={editDep.date_of_birth || ''} onChange={v => setEditDep({ ...editDep, date_of_birth: v })} /></Field>
                <Field label="Gender"><Input value={editDep.gender || ''} onChange={v => setEditDep({ ...editDep, gender: v })} /></Field>
                <Field label="SSN"><Input value={editDep.ssn || ''} onChange={v => setEditDep({ ...editDep, ssn: v })} placeholder={editDep.id ? `(••••${editDep.ssn_last4 || ''} - leave blank to keep)` : ''} /></Field>
              </Grid>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginTop: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!editDep.is_covered_health} onChange={e => setEditDep({ ...editDep, is_covered_health: e.target.checked })} />Health
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!editDep.is_covered_dental} onChange={e => setEditDep({ ...editDep, is_covered_dental: e.target.checked })} />Dental
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!editDep.is_covered_vision} onChange={e => setEditDep({ ...editDep, is_covered_vision: e.target.checked })} />Vision
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={!!editDep.is_covered_life} onChange={e => setEditDep({ ...editDep, is_covered_life: e.target.checked })} />Life
                </label>
              </div>
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setEditDep(null)} style={{ padding: '8px 16px', borderRadius: 6, fontSize: 12, fontWeight: 600, border: '1px solid var(--border-default)', background: '#fff', cursor: 'pointer' }}>Cancel</button>
                <button onClick={() => saveDependent(editDep)} disabled={saving} style={{ padding: '8px 18px', borderRadius: 6, fontSize: 12, fontWeight: 600, background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff', border: 'none', cursor: saving ? 'wait' : 'pointer' }}>
                  {saving ? 'Saving…' : 'Save dependent'}
                </button>
              </div>
            </div>
          )}
        </Section>
      )}

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24,
          padding: '10px 16px', borderRadius: 6, fontSize: 13,
          background: toast.ok ? '#d1fae5' : '#fee2e2',
          color: toast.ok ? '#065f46' : '#991b1b',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}>{toast.msg}</div>
      )}
    </PageLayout>
  );
}

// ── small UI primitives kept local so this file ships standalone ──

function Section({ children }: { children: React.ReactNode }) {
  return <div style={{ background: '#fff', border: '1px solid var(--border-default)', borderRadius: 8, padding: 24 }}>{children}</div>;
}

function Subhead({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy-900)', margin: '20px 0 12px', letterSpacing: '-0.2px' }}>{children}</div>;
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>{children}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, color: 'var(--text-secondary)' }}>
      <span style={{ fontWeight: 600 }}>{label}</span>
      {children}
    </label>
  );
}

function Input({ value, onChange, type = 'text', placeholder = '' }:
  { value: string | number | null | undefined; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <input type={type} value={value ?? ''} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: '8px 10px', fontSize: 13, borderRadius: 6,
        border: '1px solid var(--border-default)', background: '#fff', color: 'var(--navy-900)',
      }}
    />
  );
}

function Select({ value, onChange, options, label }:
  { value: string; onChange: (v: string) => void; options: string[]; label?: string }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}
      aria-label={label || 'Select option'}
      style={{
        padding: '8px 10px', fontSize: 13, borderRadius: 6,
        border: '1px solid var(--border-default)', background: '#fff', color: 'var(--navy-900)',
      }}>
      {options.map(o => <option key={o} value={o}>{o || '—'}</option>)}
    </select>
  );
}

function SaveBar({ saving, onSave }: { saving: boolean; onSave: () => void }) {
  return (
    <div style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
      <button onClick={onSave} disabled={saving}
        style={{
          padding: '10px 22px', borderRadius: 6, fontSize: 13, fontWeight: 600,
          background: saving ? 'var(--text-hint)' : '#1e3a5f', color: '#fff',
          border: 'none', cursor: saving ? 'wait' : 'pointer',
        }}>
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );
}

function AddressForm({ value, onChange }: { value: Address; onChange: (v: Address) => void }) {
  const set = (patch: Partial<Address>) => onChange({ ...value, ...patch });
  return (
    <Grid>
      <Field label="Street address"><Input value={value.street_line1 || ''} onChange={v => set({ street_line1: v })} /></Field>
      <Field label="Apt / suite"><Input value={value.street_line2 || ''} onChange={v => set({ street_line2: v })} /></Field>
      <Field label="City"><Input value={value.city || ''} onChange={v => set({ city: v })} /></Field>
      <Field label="State / province"><Input value={value.state_province || ''} onChange={v => set({ state_province: v })} /></Field>
      <Field label="Postal code"><Input value={value.postal_code || ''} onChange={v => set({ postal_code: v })} /></Field>
      <Field label="Country"><Input value={value.country || ''} onChange={v => set({ country: v })} /></Field>
    </Grid>
  );
}

function anyAddrField(a: Address): boolean {
  return !!(a.street_line1 || a.street_line2 || a.city || a.state_province || a.postal_code || a.country);
}
